import { Injectable, signal, computed } from '@angular/core';
import { Clerk } from '@clerk/clerk-js';
import { ui } from '@clerk/ui/no-rhc';
import type { OrganizationResource, SessionResource, UserResource } from '@clerk/shared/types';
import { CLERK_APPEARANCE, CLERK_TEXTS } from './clerk-configuration';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class Auth {
  private clerk: Clerk | null = null;

  private readonly _isLoaded = signal(false);
  private readonly _user = signal<UserResource | null>(null);
  private readonly _session = signal<SessionResource | null>(null);
  private readonly _organization = signal<OrganizationResource | null>(null);

  readonly isLoaded = this._isLoaded.asReadonly();
  readonly user = this._user.asReadonly();
  readonly session = this._session.asReadonly();
  readonly organization = this._organization.asReadonly();
  readonly isSignedIn = computed(() => this._user() !== null);

  readonly userName = computed(() => {
    const u = this._user();
    if (!u) return '';
    return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || '';
  });

  readonly userImageUrl = computed(() => this._user()?.imageUrl ?? '');
  readonly orgName = computed(() => this._organization()?.name ?? '');
  readonly orgImageUrl = computed(() => this._organization()?.imageUrl ?? '');
  readonly hasMultipleOrganizations = computed(
    () => (this._user()?.organizationMemberships?.length ?? 0) > 1,
  );

  async init(): Promise<void> {
    this.clerk = new Clerk(environment.clerkPublishableKey);
    await this.clerk.load({
      ui,
      appearance: {
        baseTheme: undefined,
      },
      localization: CLERK_TEXTS,
    });

    this.syncState();

    this.clerk.addListener(() => {
      this.syncState();
    });

    this._isLoaded.set(true);
  }

  private syncState(): void {
    if (!this.clerk) return;
    this._user.set(this.clerk.user ?? null);
    this._session.set(this.clerk.session ?? null);
    this._organization.set(this.clerk.organization ?? null);
  }

  signIn(): void {
    if (!this.clerk) return;

    this.clerk.openSignIn({
      appearance: {
        variables: CLERK_APPEARANCE,
      },
      signUpForceRedirectUrl: '/getting-started',
      withSignUp: true,
    });
  }

  signUp(): void {
    if (!this.clerk) return;

    this.clerk.openSignUp({
      appearance: {
        variables: CLERK_APPEARANCE,
      },
      forceRedirectUrl: '/getting-started',
    });
  }

  async signOut(): Promise<void> {
    await this.clerk?.signOut();
    this.syncState();
  }

  async getToken(): Promise<string | null> {
    const session = this.clerk?.session;
    if (!session) {
      return null;
    }

    return session.getToken();
  }

  openUserProfile(): void {
    this.clerk?.openUserProfile({
      apiKeysProps: {
        hide: true,
      },
      appearance: {
        variables: CLERK_APPEARANCE,
      },
    });
  }

  getClerk(): Clerk | null {
    return this.clerk;
  }
}
